import { CalendarUnit, DataType } from '@doss/prisma/types'
import dayjs, { Dayjs } from 'dayjs'
import 'dayjs/locale/de.js'
import customParseFormat from 'dayjs/plugin/customParseFormat.js'
import duration from 'dayjs/plugin/duration.js'
import localizedFormat from 'dayjs/plugin/localizedFormat.js'
import relativeTime from 'dayjs/plugin/relativeTime.js'
import timezone from 'dayjs/plugin/timezone.js'
import utc from 'dayjs/plugin/utc.js'
import weekOfYear from 'dayjs/plugin/weekOfYear.js'
import { DATE_FILTER_STRING_FORMAT, isValidDate, toDateTimeObject } from './dates.js'
import { isDateColumn } from './table-column.js'

// Importing extend directly from dayjs causes esm/commonjs issues
dayjs.extend(customParseFormat)
dayjs.extend(utc)
dayjs.extend(relativeTime)
dayjs.extend(timezone)
dayjs.extend(localizedFormat)
dayjs.extend(weekOfYear)
dayjs.extend(duration)

export function getZeroIndexedCurrentQuarterNumber(
  zeroIndexedMonth: number,
  startOfFiscalYearAsNumber = 0,
): number {
  const monthOffset = (zeroIndexedMonth - startOfFiscalYearAsNumber + 12) % 12 // Months since the start of the fiscal year
  return Math.floor(monthOffset / 3) // Determine the 0-indexed quarter within the fiscal year
}

/**
 * Given a date and a start of fiscal year month (0-indexed), returns the start of the quarter for the date
 * All references to months and quarters are 0-indexed because dayjs months are 0-indexed
 */
export function getStartOfQuarterDayJsTimestamp(date: Dayjs, startOfFiscalYearAsNumber = 0): Dayjs {
  const month = date.month()
  const quarter = getZeroIndexedCurrentQuarterNumber(month, startOfFiscalYearAsNumber)
  const startOfQuarterMonth = (startOfFiscalYearAsNumber + quarter * 3) % 12 // 0-indexed month for dayjs
  // If the fiscal year starts in September (8), and the current month is January (0)
  // This essentially swaps out the month of the date with quarter month, resulting in December (11) of this year
  const startOfQuarterTimestamp = date.month(startOfQuarterMonth).startOf('month')
  // The start of the quarter is actually December (11) in the previous year, 11 > 0, so we subtract 1 year
  if (startOfQuarterMonth > month) {
    return startOfQuarterTimestamp.subtract(1, 'year')
  }
  return startOfQuarterTimestamp
}
export function getStartOfWeekDayJsTimestamp(date: Dayjs, startOfWeekAsNumber = 0): Dayjs {
  const dayOfWeek = date.day()
  const daysToSubtract = (dayOfWeek - startOfWeekAsNumber + 7) % 7
  return date.subtract(daysToSubtract, 'day').startOf('day')
}

/*
 *Floors a dayjs date to the specified unit
 */
export function floorDayJSDate(
  dateTime: Dayjs,
  floorUnit: string,
  startOfWeekAsNumber = 0,
  startOfFiscalYearAsNumber = 0,
): Dayjs {
  switch (floorUnit) {
    case CalendarUnit.MINUTE:
      return dateTime.startOf('minute')
    case CalendarUnit.HOUR:
      return dateTime.startOf('hour')
    case CalendarUnit.DAY:
      return dateTime.startOf('day')
    case CalendarUnit.WEEK:
      return getStartOfWeekDayJsTimestamp(dateTime, startOfWeekAsNumber)
    case CalendarUnit.MONTH:
      return dateTime.startOf('month')
    case CalendarUnit.QUARTER:
      return getStartOfQuarterDayJsTimestamp(dateTime, startOfFiscalYearAsNumber)
    case CalendarUnit.YEAR:
      return dateTime.startOf('year')
    default:
      throw new Error(`Unsupported floor unit: ${floorUnit}`)
  }
}

export type OrgDateTimeConfig = {
  timeZone?: string
  startOfWeekDay?: number
  startOfFiscalYearMonth?: number
}

/**
 * Checks if a string is a valid date filter operand data in the format YYYY-MM-DD or 'now'
 */
export function isValidDateFilterString(dateString: string): boolean {
  return isValidDate(dateString) || dateString === 'now'
}

/**
 * Given a date string in YYYY-MM-DD format or 'now',
 * return a date time filter string in ISO format, based on the column data type
 *
 * If the time is 'now', it will return the current date in the organization's timezone, converted to utc midnight if the column data type is DATE
 * If the time is a date, it will return the start of the day in the organization's timezone, converted to utc midnight if the column data type is DATE
 */
export function getDateOnlyTimestampByColumnDataType(
  dateString: string,
  columnDataType: DataType,
  dayOffset = 0,
  orgDateTimeConfig?: OrgDateTimeConfig,
): string {
  if (!isValidDateFilterString(dateString)) {
    throw new Error(`Invalid date string: ${dateString} to convert to date time filter string`)
  }
  if (!isDateColumn(columnDataType)) {
    throw new Error(
      `Invalid column data type: ${columnDataType} to convert to date time filter string`,
    )
  }
  let date: Dayjs
  if (dateString === 'now') {
    date = dayjs().tz(orgDateTimeConfig?.timeZone).startOf('day')
  } else {
    date = toDateTimeObject(dateString, {
      timeZone: orgDateTimeConfig?.timeZone,
      customFormat: DATE_FILTER_STRING_FORMAT,
    }).startOf('day')
  }
  if (columnDataType === DataType.DATE || columnDataType === DataType.FORMULA_DATE) {
    date = date.utc(true)
  }
  if (dayOffset !== 0) {
    date = date.add(dayOffset, 'day').startOf('day') // Round again in case of daylight savings
  }
  return date.toISOString()
}
